import { styled } from '@linaria/react';
import { useMemo } from 'react';
import type { ArtifactComponentProps } from '../artifact-registry';
import { parseFreeformConfig, parseFreeformData } from './freeform-chart-artifact.helpers';
import { EmptyState } from './freeform-chart-shared';
import { FreeformDonut } from './freeform-donut-chart';
import { FreeformTable } from './freeform-table';
import { FreeformXYChart } from './freeform-xy-chart';

/**
 * Renders the Flora chat artifact view for freeform chart data supplied
 * directly by the model (`render_chart` tool): line, area, bar, stacked bar,
 * donut pie, or table — all driven by one columnar `{columns, rows}` payload
 * with presentation state (chart_type, subtitle, axis labels) in `config`.
 *
 * @param props - Standard Flora artifact component props.
 * @returns The chart, donut, or table view for the payload.
 */
export function FreeformChartArtifact({ data, config }: ArtifactComponentProps) {
  const payload = useMemo(() => parseFreeformData(data), [data]);
  const parsedConfig = useMemo(() => parseFreeformConfig(config), [config]);

  if (!payload || !parsedConfig) {
    return <EmptyState>No data to display</EmptyState>;
  }

  return (
    <ChartContainer>
      {parsedConfig.subtitle && <Subtitle>{parsedConfig.subtitle}</Subtitle>}
      {parsedConfig.chartType === 'table' ? (
        <FreeformTable payload={payload} />
      ) : // the "pie" chart type renders as a donut
      parsedConfig.chartType === 'pie' ? (
        <FreeformDonut payload={payload} config={parsedConfig} />
      ) : (
        <FreeformXYChart payload={payload} config={parsedConfig} />
      )}
    </ChartContainer>
  );
}

const ChartContainer = styled.div`
  width: 100%;
`;

const Subtitle = styled.div`
  font-size: 12px;
  color: var(--ink-lighter);
  padding: 0 4px 10px;
  overflow-wrap: anywhere;
`;
